import { useEffect, type RefObject } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { isApp } from '../utils/platform'
import { isAnySheetOpen } from '../utils/sheetOpenState'
import { isComposing } from '../utils/composingState'
import { goBackOrHome, isFromNotification } from '../utils/navigation'

/**
 * iOS 네이티브 스타일 인터랙티브 스와이프 뒤로가기.
 *
 * - 화면 어디서든 오른쪽 스와이프로 뒤로가기 (가로 슬라이더 위에서는 가장자리만 허용)
 * - 스와이프 중 현재 화면이 손가락을 따라 밀리고, 뒤에 이전 화면 스냅샷이
 *   패럴랙스로 드러남. 충분히 밀거나 빠르게 플릭하면 완료, 아니면 원위치 복귀.
 * - 이전 화면은 앞으로 이동(pushState)하는 순간 DOM 스냅샷으로 캡처해 둔 것을 사용.
 */

/** 가로 슬라이더 위에서 스와이프 시작 시 허용되는 왼쪽 가장자리 폭(px) */
const EDGE_WIDTH = 40
/** 제스처 방향 판정 전 허용 이동량(px) — 이 안에서는 방향 미확정 */
const SLOP = 12
/** 화면 폭 대비 이 비율 이상 밀면 뒤로가기 확정 */
const COMMIT_RATIO = 0.33
/** 플릭 판정 속도(px/ms) — 이 속도 이상이면 거리가 짧아도 확정 */
const COMMIT_VELOCITY = 0.6
/** 이전 화면 패럴랙스 비율 (iOS 기본 느낌: 30%) */
const PARALLAX = 0.3
/** 이전 화면 위 딤 최대 불투명도 */
const DIM_OPACITY = 0.2
/** 보관할 스냅샷 최대 개수 */
const MAX_SNAPSHOTS = 5
/**
 * 스냅샷에서 걷어낼 오버레이의 z-index 하한.
 *
 * 앱의 바텀시트·모달·코치마크는 전부 1040 이상을 쓰고, 화면에 늘 붙어 있는
 * AppBar·BottomNav는 z-index를 아예 안 준다. 그래서 이 선 위의 fixed 요소는
 * "지금 떠 있는 일시적인 것"으로 봐도 된다.
 */
const OVERLAY_MIN_Z = 1000
/** 앱 기본 배경색(아이보리, bg-backgroundColorBg2) — 스와이프 중 드러나는 영역·스냅샷 배경.
 *  #fff로 두면 뒤 화면이 흰색으로 떠 보이므로 앱 배경색으로 맞춘다. */
const APP_BG = '#FAF6EF'

const BLOCKED_PATHS = ['/', '/login', '/splash']

function isBlockedPath(pathname: string): boolean {
  return (
    BLOCKED_PATHS.includes(pathname) ||
    // 메인 탭(피드/랭킹/맵/마이)은 내비게이션 루트 — 뒤로가기 비활성 (인스타그램 방식)
    pathname.startsWith('/main-') ||
    // 리뷰 작성: 점수 슬라이더 등 가로 드래그 UI와 제스처 충돌 방지
    pathname.startsWith('/store-review') ||
    // 리뷰 완료(영수증): 완료된 리뷰 화면에서 뒤로가기 제스처 비활성
    pathname.startsWith('/review-complete') ||
    // 사진 비율 편집: 크롭 드래그와 제스처 충돌 방지
    pathname.startsWith('/photo-edit') ||
    // 회원가입 단계형 화면(휴대폰 입력·아이디 등): 스와이프 뒤로가기 비활성
    pathname.startsWith('/register') ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/admin')
  )
}

// ---------------------------------------------------------------------------
// 스냅샷 관리 (모듈 싱글턴)
// ---------------------------------------------------------------------------

interface Snapshot {
  el: HTMLElement
  /** [querySelectorAll('*') 인덱스, scrollTop, scrollLeft] — 부착 후 복원 */
  scrolls: Array<[number, number, number]>
  winScrollY: number
  /**
   * 캡처 시점 원본의 크기.
   *
   * 앱 레이아웃은 루트의 고정 높이에서 내려오는 height:100%/flex 사슬로
   * 스크롤 영역의 높이를 정한다. 복제본을 높이 없는 컨테이너에 넣으면 그
   * 사슬이 끊겨 목록이 통째로 펼쳐지고, 스크롤 영역이 아예 안 생긴다.
   * 그러면 scrollTop을 넣어도 소용이 없어 스냅샷이 늘 맨 위로 보인다.
   */
  width: number
  height: number
}

/**
 * 스냅샷에서 걷어낼 요소에 달아두는 표시.
 *
 * 캡처할 때 바로 지우지 않고 표시만 해둔다. scrolls가 querySelectorAll('*')의
 * 인덱스로 원본과 복제본을 맞추기 때문에, 먼저 지우면 그 뒤 요소들의 인덱스가
 * 밀려 엉뚱한 곳에 스크롤이 복원된다. 실제로 쓸 때(부착 후 스크롤 복원 뒤)
 * 지운다.
 */
const OMIT_ATTR = 'data-swipe-omit'

/** history idx → 그 시점 화면의 DOM 스냅샷 */
const snapshots = new Map<number, Snapshot>()
let snapshotSource: HTMLElement | null = null
let historyPatched = false

/** react-router v6/v7이 history.state에 기록하는 엔트리 인덱스 */
function currentHistoryIdx(): number {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx
  return typeof idx === 'number' ? idx : 0
}

function takeSnapshot() {
  // 네이티브 앱에서만 스냅샷/제스처 사용 (웹은 불필요한 DOM 복제 회피)
  if (!isApp()) return
  const src = snapshotSource
  if (!src) return
  try {
    const clone = src.cloneNode(true) as HTMLElement
    // 중복 id로 인한 getElementById 오동작 방지 (스냅샷은 순수 시각용)
    clone.removeAttribute('id')
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'))

    // cloneNode는 스크롤 위치를 복사하지 않으므로 따로 수집해 부착 후 복원
    const scrolls: Array<[number, number, number]> = []
    const els = src.querySelectorAll('*')
    for (let i = 0; i < els.length; i++) {
      const el = els[i] as HTMLElement
      if (el.scrollTop || el.scrollLeft) {
        scrolls.push([i, el.scrollTop, el.scrollLeft])
      }
    }

    /*
     * 전환하는 순간 열려 있던 바텀시트·모달은 빼고 찍는다.
     *
     * 왜 빼나
     * -------
     * 시트에서 다른 화면으로 넘어가는 코드는 대개 이렇게 생겼다.
     *
     *   setIsActionModalOpen(false)   // React가 나중에 반영한다
     *   navigate('/review-detail/1')  // pushState → 여기서 스냅샷을 찍는다
     *
     * setState는 이 핸들러가 끝나야 DOM에 반영되므로, 스냅샷을 찍는 시점의
     * 화면에는 시트가 아직 떠 있다. 그래서 되돌아올 때 이미 닫힌 시트가
     * 뒤 화면에 그려졌다(랭킹에서 '기록 보기'로 들어갔다 나오면 액션 시트가
     * 다시 보였다).
     *
     * 시트의 열림 상태는 전부 화면 안의 useState라 되돌아오면 어차피 닫힌
     * 채로 다시 그려진다. 즉 빼는 쪽이 실제로 보게 될 화면과 같다.
     *
     * getComputedStyle은 원본에서만 읽을 수 있다 — 복제본은 문서 밖이라
     * 클래스가 풀리지 않는다. 그래서 원본을 훑고 같은 인덱스의 복제본에
     * 표시만 남긴다.
     */
    const cloneEls = clone.querySelectorAll('*')
    for (let i = 0; i < els.length; i++) {
      const style = window.getComputedStyle(els[i] as HTMLElement)
      if (style.position !== 'fixed') continue
      const z = parseInt(style.zIndex, 10)
      if (Number.isNaN(z) || z < OVERLAY_MIN_Z) continue
      cloneEls[i]?.setAttribute(OMIT_ATTR, '')
    }

    snapshots.set(currentHistoryIdx(), {
      el: clone,
      scrolls,
      winScrollY: window.scrollY || document.documentElement.scrollTop || 0,
      width: src.offsetWidth,
      height: src.offsetHeight,
    })

    if (snapshots.size > MAX_SNAPSHOTS) {
      const keys = Array.from(snapshots.keys()).sort((a, b) => a - b)
      while (snapshots.size > MAX_SNAPSHOTS) {
        snapshots.delete(keys.shift() as number)
      }
    }
  } catch {
    // 스냅샷 실패 시 제스처는 폴백 배경(딤 처리된 단색)으로 동작
  }
}

/**
 * 네이티브(알림 이동)가 부른 pushState인지.
 *
 * 앱은 알림 목적지로 보낼 때 `history.pushState({}, '', path)`를 부른다.
 * react-router는 언제나 {usr, key, idx}를 넣으므로 빈 객체면 네이티브다.
 * (앱이 제대로 된 state를 보내도록 고쳐지면 이 분기에 안 걸리고 그냥 통과한다)
 */
function isNativePushState(state: unknown): boolean {
  return (
    !!state &&
    typeof state === 'object' &&
    Object.keys(state as object).length === 0
  )
}

/** pushState 직전(= React가 DOM을 교체하기 전)에 떠나는 화면을 캡처 */
function patchHistoryOnce() {
  if (historyPatched) return
  historyPatched = true
  const original = window.history.pushState.bind(window.history)
  window.history.pushState = function (...args: Parameters<History['pushState']>) {
    const [state, , url] = args

    // 알림 이동은 네이티브 앱에서만 일어난다. 웹 브라우저에서는 손대지 않아
    // 혹시 다른 코드가 빈 state로 pushState를 불러도 영향이 없게 한다.
    if (isApp() && isNativePushState(state) && url != null) {
      const next = new URL(String(url), window.location.href)

      // 지금 보고 있는 화면과 같은 목적지: URL이 그대로라 라우터의 location이
      // 안 바뀌어 리렌더가 없고(알림을 눌러도 무반응), 같은 URL이 히스토리에
      // 중복으로 쌓여 이후 스와이프 백이 "돌아갈 수 있다"고 오판한다 →
      // 전환 애니메이션만 돌고 화면이 그 상태로 굳는다. 통째로 다시 읽는다.
      if (next.pathname === window.location.pathname) {
        window.location.replace(next.href)
        return
      }

      // 다른 목적지면 react-router가 쓰는 state 형태를 채워 넣는다.
      // {}로 덮이면 idx가 사라져 뒤로가기·스와이프 백 판별이 어긋난다.
      // fromNotification은 goBackOrHome이 읽어, 이 화면에서의 뒤로가기를
      // 직전 화면이 아니라 홈으로 보내는 데 쓴다(알림은 진입점이므로).
      args[0] = {
        usr: null,
        key: Math.random().toString(36).slice(2, 10),
        idx: currentHistoryIdx() + 1,
        fromNotification: true,
      }
    }

    takeSnapshot()
    return original(...args)
  }
}

/** 표시해 둔 오버레이를 걷어낸다. 스크롤 복원이 끝난 뒤에 부른다. */
function stripOverlays(clone: HTMLElement) {
  clone.querySelectorAll(`[${OMIT_ATTR}]`).forEach((el) => el.remove())
}

function applyScrolls(clone: HTMLElement, scrolls: Snapshot['scrolls']) {
  if (!scrolls.length) return
  const els = clone.querySelectorAll('*')
  for (const [i, top, left] of scrolls) {
    const el = els[i] as HTMLElement | undefined
    if (el) {
      el.scrollTop = top
      el.scrollLeft = left
    }
  }
}

// ---------------------------------------------------------------------------
// 충돌 감지: 가로 슬라이더/캐러셀 위에서는 가장자리 스와이프만 허용
// ---------------------------------------------------------------------------

/**
 * 가장자리에서 시작해도 뒤로가기를 막아야 하는 자리인가.
 *
 * 슬라이더 위에서는 원래 캐러셀에 양보하지만, 왼쪽 40px 안에서 시작한
 * 손짓만은 뒤로가기로 넘긴다. 그런데 사진 여러 장 중 두 번째 이후에서는
 * 오른쪽으로 미는 손짓이 '이전 사진'을 뜻한다. 그때는 가장자리에서 시작해도
 * 캐러셀이 가져가야 한다.
 *
 * 첫 장(또는 한 장뿐)일 때는 이 표시가 붙지 않으므로 지금처럼 뒤로 간다.
 */
function blocksSwipeBack(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null
  while (el && el !== document.body) {
    if (el.hasAttribute('data-swipe-back-block')) return true
    el = el.parentElement
  }

  return false
}

function startsInHorizontalSlider(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null

  /*
   * 슬라이더가 이 손짓을 안 쓰겠다고 표시한 경우.
   *
   * 사진 뷰어의 첫 장에서는 오른쪽으로 밀어도 넘어갈 이전 사진이 없다.
   * 그때까지 가장자리 40px만 허용하면, 사용자는 뒤로 가려고 밀었는데
   * 아무 일도 일어나지 않는다. 표시가 있으면 슬라이더가 아닌 것으로 보고
   * 화면 어디서 시작하든 뒤로가기로 넘긴다.
   *
   * .splide 클래스보다 먼저 확인해야 한다 — 표시는 슬라이더 바깥 틀에
   * 붙는데, 위로 올라가다 보면 .splide를 먼저 만나기 때문이다.
   */
  for (let p = el; p && p !== document.body; p = p.parentElement) {
    if (p.hasAttribute('data-swipe-back-allow')) return false
  }

  while (el && el !== document.body) {
    // 명시적 제외 마커 (transform 기반 커스텀 슬라이더용)
    if (el.hasAttribute('data-swipe-back-ignore')) return true
    // Splide 캐러셀 (ReviewImageSlider 등)
    if (el.classList.contains('splide')) return true
    // 네이티브 가로 스크롤 영역 (overflow-x-auto 등)
    if (el instanceof HTMLElement && el.scrollWidth > el.clientWidth + 1) {
      const ox = getComputedStyle(el).overflowX
      if (ox === 'auto' || ox === 'scroll') return true
    }
    el = el.parentElement
  }
  return false
}

/**
 * 지금 사용자가 텍스트를 고르고 있는가.
 *
 * 댓글이나 기록 본문을 길게 눌러 글자를 선택한 뒤 핸들을 옆으로 끌면,
 * 그 드래그가 오른쪽으로 향하는 순간 뒤로가기 제스처로 잡혀 화면이 넘어갔다.
 * 글자를 복사하려던 사람에게는 앱이 제멋대로 뒤로 가는 것으로 보인다.
 *
 * 선택 영역이 살아 있는 동안에는 제스처를 시작하지 않는다.
 * iOS·안드로이드 웹뷰 모두 길게 누르면 먼저 한 단어가 선택되므로,
 * 핸들을 끌기 시작하는 시점에는 이미 선택이 존재한다.
 */
function hasActiveTextSelection(): boolean {
  const sel = window.getSelection()

  return !!sel && !sel.isCollapsed && sel.toString().trim().length > 0
}

/**
 * 글자를 직접 다루는 요소에서 시작한 터치인가.
 *
 * 입력칸 안에서는 커서를 옮기려고 옆으로 끄는 일이 잦다.
 * 아직 선택이 없더라도 제스처를 넘기지 않는다.
 */
function startsInTextField(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null
  while (el && el !== document.body) {
    const tag = el.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA') return true
    if (el instanceof HTMLElement && el.isContentEditable) return true
    el = el.parentElement
  }

  return false
}

// ---------------------------------------------------------------------------
// 훅 본체
// ---------------------------------------------------------------------------

export function useSwipeBack(containerRef: RefObject<HTMLDivElement | null>) {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // 스냅샷 캡처 대상 등록 (차단 경로에서도 캡처는 되어야 함)
    snapshotSource = container
    patchHistoryOnce()

    if (isBlockedPath(location.pathname)) return

    type GestureState = 'idle' | 'pending' | 'active' | 'animating'
    let state: GestureState = 'idle'
    let startX = 0
    let startY = 0
    let lastX = 0
    let lastT = 0
    let velocity = 0
    let width = 1
    let pendingDx = 0
    let raf = 0
    let finishTimer = 0
    let overlay: HTMLDivElement | null = null
    let snapWrap: HTMLDivElement | null = null
    let dim: HTMLDivElement | null = null

    const cleanupVisuals = () => {
      if (raf) {
        cancelAnimationFrame(raf)
        raf = 0
      }
      if (finishTimer) {
        clearTimeout(finishTimer)
        finishTimer = 0
      }
      container.style.transform = ''
      container.style.transition = ''
      container.style.willChange = ''
      container.style.position = ''
      container.style.zIndex = ''
      container.style.boxShadow = ''
      container.style.background = ''
      overlay?.remove()
      overlay = snapWrap = dim = null
    }

    /** 제스처 확정 시 이전 화면 오버레이 생성 + 현재 화면 이동 준비 */
    const begin = () => {
      width = window.innerWidth || 1
      // 뒤에 깔 화면은 실제 목적지와 같아야 한다. 알림으로 들어온 화면에서는
      // goBackOrHome이 직전 화면이 아니라 홈으로 보내므로, 직전 화면 스냅샷을
      // 쓰면 엉뚱한 화면이 밀려들어왔다가 홈으로 튄다 → 단색 배경으로 둔다.
      const snap = isFromNotification()
        ? undefined
        : snapshots.get(currentHistoryIdx() - 1)

      overlay = document.createElement('div')
      overlay.setAttribute('aria-hidden', 'true')
      overlay.style.cssText = `position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;background:${APP_BG}`

      snapWrap = document.createElement('div')
      snapWrap.style.cssText = `position:absolute;inset:0;overflow:hidden;background:${APP_BG};will-change:transform;transform:translate3d(${-PARALLAX * 100}%,0,0)`

      if (snap) {
        const content = document.createElement('div')
        content.style.cssText = `position:absolute;top:${-snap.winScrollY}px;left:0;right:0;height:${snap.height}px`
        // 복제본에 캡처 당시 크기를 그대로 준다. 그래야 내부 스크롤 영역이
        // 원본과 같은 높이로 잡히고, scrollTop이 실제로 의미를 갖는다.
        snap.el.style.width = `${snap.width}px`
        snap.el.style.height = `${snap.height}px`
        content.appendChild(snap.el)
        snapWrap.appendChild(content)
        // 스크롤 복원은 아래에서 overlay를 document에 붙인 뒤에 한다.
        // 문서 밖 요소에 scrollTop을 넣으면 레이아웃이 없어 조용히 무시된다.
      }

      dim = document.createElement('div')
      dim.style.cssText = `position:absolute;inset:0;background:#000;opacity:${DIM_OPACITY}`

      overlay.appendChild(snapWrap)
      overlay.appendChild(dim)
      document.body.appendChild(overlay)

      // 문서에 붙어 레이아웃이 잡힌 뒤라야 scrollTop이 실제로 적용된다.
      // (붙기 전에 넣으면 스냅샷이 항상 맨 위로 그려져, 스와이프 백에서
      //  직전 화면의 상단이 보였다가 제자리로 튀는 것처럼 보인다)
      if (snap) {
        applyScrolls(snap.el, snap.scrolls)
        // 인덱스를 쓰는 스크롤 복원이 끝난 뒤라야 안전하다
        stripOverlays(snap.el)
      }

      const bodyBg = getComputedStyle(document.body).backgroundColor
      container.style.background =
        bodyBg && bodyBg !== 'rgba(0, 0, 0, 0)' && bodyBg !== 'transparent'
          ? bodyBg
          : APP_BG
      container.style.position = 'relative'
      container.style.zIndex = '1'
      container.style.willChange = 'transform'
      container.style.transition = 'none'
      container.style.boxShadow = '-4px 0 24px rgba(0,0,0,0.18)'
    }

    const render = () => {
      raf = 0
      if (state !== 'active') return
      const p = pendingDx / width
      container.style.transform = `translate3d(${pendingDx}px,0,0)`
      if (snapWrap) {
        snapWrap.style.transform = `translate3d(${-PARALLAX * (1 - p) * 100}%,0,0)`
      }
      if (dim) {
        dim.style.opacity = String(DIM_OPACITY * (1 - p))
      }
    }

    /** 손을 뗀 뒤: 완료(뒤로가기) 또는 취소(원위치) 애니메이션 */
    const finish = (commit: boolean) => {
      state = 'animating'
      if (raf) {
        cancelAnimationFrame(raf)
        raf = 0
      }
      const remaining = commit ? 1 - pendingDx / width : pendingDx / width
      const dur = Math.max(120, Math.min(280, 280 * remaining))
      const ease = 'cubic-bezier(0.2, 0.8, 0.3, 1)'

      container.style.transition = `transform ${dur}ms ${ease}`
      if (snapWrap) snapWrap.style.transition = `transform ${dur}ms ${ease}`
      if (dim) dim.style.transition = `opacity ${dur}ms ${ease}`
      // 트랜지션 적용을 위한 강제 리플로우
      void container.offsetWidth

      if (commit) {
        container.style.transform = `translate3d(${width}px,0,0)`
        if (snapWrap) snapWrap.style.transform = 'translate3d(0,0,0)'
        if (dim) dim.style.opacity = '0'
      } else {
        container.style.transform = 'translate3d(0,0,0)'
        if (snapWrap) {
          snapWrap.style.transform = `translate3d(${-PARALLAX * 100}%,0,0)`
        }
        if (dim) dim.style.opacity = String(DIM_OPACITY)
      }

      finishTimer = window.setTimeout(() => {
        finishTimer = 0
        if (commit) {
          // 라우트 전환 → location 변경으로 이 effect가 cleanup되며
          // transform/오버레이가 같은 프레임에 정리되어 깜빡임 없음
          goBackOrHome(navigate)
        } else {
          cleanupVisuals()
          state = 'idle'
        }
      }, dur + 30)
    }

    const onTouchStart = (e: TouchEvent) => {
      if (state === 'animating') return
      state = 'idle'
      // 네이티브 앱 웹뷰에서만 활성화 — 브리지가 늦게 주입되는 기기/플랫폼에서도
      // 동작하도록 마운트 시점이 아닌 매 터치마다 확인 (일부 기종 제스처 OFF 방지)
      if (!isApp()) return
      // 바텀시트가 열려있으면 화면 전역 뒤로가기 제스처 자체를 차단
      // (시트는 자체 뒤로가기/드래그-닫기로 처리 — 뒤 화면이 밀려나면 안 됨)
      if (isAnySheetOpen()) return
      // 댓글 등을 쓰는 중이면 제스처를 재운다.
      // 쓰던 글자를 복사하려고 옆으로 끄는 손짓이 뒤로가기로 잡혀
      // 작성 중이던 내용이 통째로 날아갔다. 뒤로가기는 상단 버튼으로 하면 된다.
      if (isComposing()) return
      // 돌아갈 히스토리가 없어도(알림 딥링크로 콜드스타트한 첫 화면) 제스처를 막지 않는다.
      // 완료 시 goBackOrHome이 홈으로 보내므로, 상단 뒤로가기 버튼과 동작이 같아진다.
      // (이전 화면 스냅샷이 없으면 딤 배경 폴백으로 전환된다)
      if (e.touches.length !== 1) return
      // 글자를 고르는 중이거나 입력칸을 다루는 중이면 양보한다.
      // 복사하려고 끄는 손짓이 뒤로가기로 잡히면 안 된다.
      if (hasActiveTextSelection()) return
      if (startsInTextField(e.target)) return
      const t = e.touches[0]
      // 앞으로 넘길 사진이 남아 있으면 가장자리에서 시작해도 캐러셀에 넘긴다
      if (blocksSwipeBack(e.target)) return
      // 가로 슬라이더 위에서는 가장자리 시작만 허용 (캐러셀에 양보)
      if (t.clientX > EDGE_WIDTH && startsInHorizontalSlider(e.target)) return
      startX = lastX = t.clientX
      startY = t.clientY
      lastT = e.timeStamp
      velocity = 0
      pendingDx = 0
      state = 'pending'
    }

    const onTouchMove = (e: TouchEvent) => {
      if (state === 'pending') {
        const t = e.touches[0]
        const dx = t.clientX - startX
        const dy = t.clientY - startY
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return
        // 방향 잠금: 세로 우세 또는 왼쪽 이동이면 포기 → 스크롤에 양보
        if (dx <= 0 || Math.abs(dy) >= Math.abs(dx)) {
          state = 'idle'
          return
        }
        // 손을 댄 뒤 길게 눌러 선택이 생겼을 수 있다.
        // 화면을 밀기 시작하기 직전에 한 번 더 확인한다.
        if (hasActiveTextSelection()) {
          state = 'idle'
          return
        }
        begin()
        state = 'active'
      }
      if (state !== 'active') return
      if (e.touches.length !== 1) {
        finish(false)
        return
      }
      if (e.cancelable) e.preventDefault()
      const t = e.touches[0]
      const now = e.timeStamp
      if (now > lastT) {
        velocity = velocity * 0.6 + ((t.clientX - lastX) / (now - lastT)) * 0.4
      }
      lastX = t.clientX
      lastT = now
      pendingDx = Math.min(Math.max(t.clientX - startX, 0), width)
      if (!raf) raf = requestAnimationFrame(render)
    }

    const onTouchEnd = () => {
      if (state === 'pending') {
        state = 'idle'
        return
      }
      if (state !== 'active') return
      const commit =
        pendingDx > width * COMMIT_RATIO ||
        (velocity > COMMIT_VELOCITY && pendingDx > 40)
      finish(commit)
    }

    const onTouchCancel = () => {
      if (state === 'active') {
        finish(false)
      } else {
        state = 'idle'
      }
    }

    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: false })
    window.addEventListener('touchend', onTouchEnd, { passive: true })
    window.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchCancel)
      cleanupVisuals()
    }
  }, [navigate, location.pathname, containerRef])
}
